import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { Search, Calendar, Target, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useState } from "react";

interface KanbanFilterBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  scoreFilter: number;
  onScoreChange: (value: number[]) => void;
  maxDaysAgo?: number | null;
  onMaxDaysAgoChange?: (days: number | null) => void;
}

export const KanbanFilterBar = ({
  searchQuery,
  onSearchChange,
  scoreFilter,
  onScoreChange,
  maxDaysAgo = 60,
  onMaxDaysAgoChange,
}: KanbanFilterBarProps) => {
  const { t } = useTranslation();
  const [daysPopoverOpen, setDaysPopoverOpen] = useState(false);
  const [scorePopoverOpen, setScorePopoverOpen] = useState(false);

  return (
    <div className="flex items-center gap-8 p-4 mb-4 bg-white dark:bg-zinc-900 rounded-sm border border-border shadow-sm">
      {/* Search input */}
      <div className="flex-1">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("kanban.filterPlaceholder") || "Search by name, company, or function..."}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8 pr-8"
          />
          {searchQuery && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              onClick={() => onSearchChange('')}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t("common.clearSearch", "Clear search")}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Days filter */}
      {onMaxDaysAgoChange && (
        <Popover open={daysPopoverOpen} onOpenChange={setDaysPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Calendar className="h-4 w-4 mr-1" />
              {maxDaysAgo === null
                ? t("kanban.daysFilter.allCandidates", "All candidates")
                : t("kanban.daysFilter.lastXDays", `Last ${maxDaysAgo} days`, { count: maxDaysAgo })}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-medium text-sm">
                {maxDaysAgo === null
                  ? t("kanban.daysFilter.showAllCandidates", "Show all candidates")
                  : t("kanban.daysFilter.showCandidatesFromLastXDays", `Show candidates from last ${maxDaysAgo} days`, { count: maxDaysAgo })}
              </span>
              <Button variant="ghost" size="sm" onClick={() => { onMaxDaysAgoChange(null); setDaysPopoverOpen(false); }}>
                {t("kanban.daysFilter.showAll", "Show all")}
              </Button>
            </div>
            <div 
              onDoubleClick={() => onMaxDaysAgoChange(60)}
              className="cursor-pointer"
              title="Double-click to reset to 60 days"
            >
              <Slider
                min={1}
                max={365}
                step={1}
                value={[maxDaysAgo || 60]}
                onValueChange={([val]) => onMaxDaysAgoChange(val)}
              />
            </div>
            <div className="flex justify-between text-xs mt-2 text-gray-500">
              <span>{t("kanban.daysFilter.min", "1")}</span>
              <span>{t("kanban.daysFilter.max", "365")}</span>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Score filter */}
      <Popover open={scorePopoverOpen} onOpenChange={setScorePopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Target className="h-4 w-4 mr-1" />
            {scoreFilter === 0 
              ? t("kanban.minScore", "Min Score")
              : `${t("kanban.minScore", "Min Score")}: ${scoreFilter}%`}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80">
          <div className="mb-4 flex items-center justify-between">
            <span className="font-medium text-sm">
              {t("kanban.scoreFilter.showCandidatesWithMinScore", "Show candidates with minimum score of {{score}}%", { score: scoreFilter })}
            </span>
            <Button variant="ghost" size="sm" onClick={() => { onScoreChange([0]); setScorePopoverOpen(false); }}>
              {t("kanban.scoreFilter.showAll", "Show all")}
            </Button>
          </div>
          <div 
            onDoubleClick={() => onScoreChange([0])}
            className="cursor-pointer"
            title="Double-click to reset to 0%"
          >
            <Slider
              min={0}
              max={100}
              step={1}
              value={[scoreFilter]}
              onValueChange={onScoreChange}
            />
          </div>
          <div className="flex justify-between text-xs mt-2 text-gray-500">
            <span>{t("kanban.scoreFilter.min", "0%")}</span>
            <span>{t("kanban.scoreFilter.max", "100%")}</span>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}; 